/**
 * @summary - Component that Provides Stock Register Edit Page
 * @author Yuvasri S
 * @since 2024-07-08
 */
import React, { useState, useEffect } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import StockDetailsEditTab from "./StockDetailsEditTab";
import StockUpdateEditTab from "./StockUpdateEditTab";
import StockAdjustmentEditTab from "./StockAdjustmentEditTab";
import { useLocation } from 'react-router-dom';
import PageTitle from "../commoncomponents/PageTitle";
import CustomDialog from "../commoncomponents/CustomDialog";

const StockRegisterEdit = () => {
    const [value, setValue] = useState(0);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [pendingTab, setPendingTab] = useState(null);

    const location = useLocation();
    const isEditMode = location.state?.isEditMode || false;
    const isViewMode = location.state?.isViewMode || false;

    const [stockDetails, setStockDetails] = useState({
        srId: '',
        itemcode: '',
        itemcodeId: '',
        itemCodeDesc: '',
        itemType: '',
        service: '',
        status: '',
        workGroup: '',
        quantity: '',
        minUnit: '',
        maxUnit: '',
        minPrice: '',
        maxPrice: '',
        currentStockLevel: '',
    });

    useEffect(() => {
        if (location.state) {
            setStockDetails({
                itemcodeId: location.state.id || '',
                itemcode: location.state.itemcode || '',
                itemCodeDesc: location.state.itemDescription || '',
                itemType: location.state.itemType || '',
                service: location.state.itemservice || '',
                status: location.state.isactive || '',
                workGroup: location.state.workgroup || '',
                quantity: location.state.quantity || '',
                minUnit: location.state.minimumstock || '',
                maxUnit: location.state.maximumstock || '',
                minPrice: location.state.minpriceperunit || '',
                maxPrice: location.state.maxpriceperunit || '',
                currentStockLevel: location.state.currentstocklevel || '',
            });
        }
    }, [location.state]);

    const handleChange = (event, newValue) => {
        if (!isViewMode && hasUnsavedChanges) {
            setCancelDialogOpen(true);
            setPendingTab(newValue);
        } else {
            setValue(newValue);
        }
    };

    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
        setPendingTab(null);
    };

    const handleCancelDialogConfirm = () => {
        setCancelDialogOpen(false);
        setHasUnsavedChanges(false);
        setValue(pendingTab);
        setPendingTab(null);
    };

    const handleStockDetails = (updatedInfo) => {
        setStockDetails((prevInfo) => ({ ...prevInfo, ...updatedInfo }));
    };

    return (
        <Box sx={{ width: '100%' }}>
            <PageTitle
                isEdit={isEditMode}
                isView={isViewMode}
                editTitle={"Edit Stock Register"}
                viewTitle={"View Stock Register"}
            />
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                <Tab label="Stock Details" />
                {!isViewMode && <Tab label="Stock Update" />}
                <Tab label="Stock Adjustment" />
            </Tabs>
            <Box sx={{ p: 3 }}>
                {value === 0 &&
                    <StockDetailsEditTab
                        stockDetails={stockDetails}
                        onStockDetailsFetch={handleStockDetails}
                        isViewMode={isViewMode}
                    />}
                {!isViewMode && value === 1 &&
                    <StockUpdateEditTab
                        stockDetails={stockDetails}
                        isViewMode={isViewMode}
                        setUnsavedChanges={setHasUnsavedChanges}
                    />}
                {(isViewMode ? value === 1 : value === 2) &&
                    <StockAdjustmentEditTab
                        stockDetails={stockDetails}
                        isViewMode={isViewMode}
                        setUnsavedChanges={setHasUnsavedChanges}
                    />}
            </Box>

            {!isViewMode && (
                <CustomDialog
                    open={cancelDialogOpen}
                    onClose={handleCancelDialogClose}
                    content="Record(s) not Saved/Updated. Do you want to continue?"
                    confirmBtn="Yes"
                    cancelBtn="No"
                    onConfirm={handleCancelDialogConfirm}
                    onCancel={handleCancelDialogClose}
                />
            )}
        </Box>
    );
};

export default StockRegisterEdit;
